import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useHomepageBanners } from "@/hooks/use-homepage";
import { useLanguage } from "@/context/LanguageContext";
import { optimizeImageUrl, responsiveSrcSet } from "@/lib/image-utils";
import { catalogPath, localizedPath, brandSlug, type Lang, type ProductType } from "@/lib/url-slugs";
import logo from "@/assets/logo.png";

// Normalize admin-entered banner link_url against the new language-prefixed URL scheme.
function normalizeBannerHref(raw: string | null | undefined, lang: Lang): string {
  const fallback = `/${lang}`;
  if (!raw) return fallback;
  const trimmed = String(raw).trim();
  if (!trimmed) return fallback;
  // External or special schemes — pass through
  if (/^(https?:|mailto:|tel:|#)/i.test(trimmed)) return trimmed;

  // Parse path + query
  let path = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  let qs = "";
  const qIdx = path.indexOf("?");
  if (qIdx >= 0) {
    qs = path.slice(qIdx);
    path = path.slice(0, qIdx);
  }
  const segs = path.split("/").filter(Boolean);
  if (segs.length === 0) return fallback + qs;

  // Already language-prefixed
  if (segs[0] === "lv" || segs[0] === "en") return path + qs;

  const TYPES = ["rackets", "shoes", "apparel", "bags", "balls", "accessories"];

  // /products?type=...&brand=...
  if (segs[0].toLowerCase() === "products") {
    const params = new URLSearchParams(qs);
    const type = params.get("type")?.toLowerCase();
    const brand = params.get("brand");
    if (type && TYPES.includes(type)) {
      params.delete("type");
      params.delete("brand");
      const rest = params.toString();
      return catalogPath(lang, type as ProductType, brand) + (rest ? `?${rest}` : "");
    }
    return `/${lang}` + qs;
  }

  // /sale, /gift-card, /cart, /contact, ... — try known static
  const STATIC_MAP: Record<string, Parameters<typeof localizedPath>[1]> = {
    sale: "sale",
    "gift-card": "giftCard",
    "davanu-karte": "giftCard",
    cart: "cart",
    grozs: "cart",
    contact: "contact",
    kontakti: "contact",
    faq: "faq",
    "biezi-uzdotie-jautajumi": "faq",
    terms: "terms",
    noteikumi: "terms",
    privacy: "privacy",
    "privatuma-politika": "privacy",
    subscription: "subscription",
    abonements: "subscription",
  };
  const staticKey = STATIC_MAP[segs[0].toLowerCase()];
  if (staticKey) return localizedPath(lang, staticKey) + qs;

  // /<brand>-raketes
  const brandMatch = segs[0].match(/^(.+)-raketes$/);
  if (brandMatch) return catalogPath(lang, "rackets", brandMatch[1]) + qs;

  // Default: prepend lang prefix
  return `/${lang}${path}` + qs;
}


const HeroBanners = () => {
  const { data: banners = [] } = useHomepageBanners();
  const { t, lang } = useLanguage();
  const [current, setCurrent] = useState(0);

  const next = useCallback(() => {
    if (banners.length > 0) setCurrent((c) => (c + 1) % banners.length);
  }, [banners.length]);

  const prev = useCallback(() => {
    if (banners.length > 0) setCurrent((c) => (c - 1 + banners.length) % banners.length);
  }, [banners.length]);

  useEffect(() => {
    if (banners.length <= 1) return;
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next, banners.length]);

  if (banners.length === 0) {
    return (
      <section className="relative bg-primary flex items-center justify-center overflow-hidden h-[250px] md:h-[420px]">
        <img src={logo} alt="" className="absolute inset-0 w-full h-full object-contain opacity-30 pointer-events-none" width={200} height={200} loading="eager" />
        <div className="relative text-center text-primary-foreground px-4">
          <h1 className="font-display text-5xl md:text-7xl font-bold mb-4">PADEL CORNER</h1>
          <p className="text-lg md:text-xl text-primary-foreground/80 mb-6">{t("home.hero.subtitle")}</p>
          <Link to={`/${lang}`} className="inline-block bg-accent text-accent-foreground font-display font-semibold px-8 py-3 rounded-lg hover:brightness-110 transition">
            {t("home.hero.cta")}
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="relative overflow-hidden bg-muted h-[250px] md:h-[420px]">
      {banners.map((b, i) => (
        <Link
          key={b.id}
          to={normalizeBannerHref(b.link_url, lang)}
          className={`absolute inset-0 transition-opacity duration-700 ${i === current ? "opacity-100 z-10" : "opacity-0 z-0"}`}
        >
          {/* Layer 1 — Blurred + darkened background (handles small images gracefully) */}
          <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
            <img
              src={optimizeImageUrl(b.image_url, { width: 800, quality: 50 })}
              alt=""
              className="absolute inset-0 w-full h-full object-cover scale-110"
              style={{ filter: "blur(24px)" }}
              width={800}
              height={600}
              loading="eager"
            />
            <div className="absolute inset-0 bg-black/40" />
            <div className="absolute inset-0 bg-green-900/50 mix-blend-multiply" />
          </div>

          {/* Layer 2 — Sharp foreground image, contained so nothing is cropped */}
          <img
            src={optimizeImageUrl(b.image_url, { width: 1600, quality: 78 })}
            srcSet={responsiveSrcSet(b.image_url, [640, 960, 1280, 1600, 1920], 78)}
            sizes="100vw"
            alt={b.title || "Banner"}
            width={1920}
            height={1280}
            loading={i === 0 ? "eager" : "lazy"}
            decoding={i === 0 ? "sync" : "async"}
            fetchPriority={i === 0 ? "high" : "low"}
            className="relative z-[1] w-full h-full object-contain object-center"
          />

          {/* Layer 3 — Text overlay */}
          {(b.title || b.subtitle) && (
            <div className="absolute inset-0 z-[2] bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-end pointer-events-none">
              <div className="container mx-auto px-4 pb-12 md:pb-20">
                {b.title && <h2 className="font-display text-3xl md:text-5xl font-bold text-white mb-2">{b.title}</h2>}
                {b.subtitle && <p className="text-lg md:text-xl text-white/80">{b.subtitle}</p>}
              </div>
            </div>
          )}
        </Link>
      ))}

      {banners.length > 1 && (
        <>
          <button onClick={(e) => { e.preventDefault(); prev(); }} aria-label="Previous banner" className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-white/80 hover:bg-white p-2 rounded-full shadow transition-colors">
            <ChevronLeft size={24} className="text-foreground" />
          </button>
          <button onClick={(e) => { e.preventDefault(); next(); }} aria-label="Next banner" className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-white/80 hover:bg-white p-2 rounded-full shadow transition-colors">
            <ChevronRight size={24} className="text-foreground" />
          </button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex gap-1">
            {banners.map((_, i) => (
              <button key={i} onClick={(e) => { e.preventDefault(); setCurrent(i); }} aria-label={`Go to banner ${i + 1}`} className="relative w-6 h-6 flex items-center justify-center">
                <span className={`block w-3 h-3 rounded-full transition-colors ${i === current ? "bg-accent" : "bg-white/60"}`} />
              </button>
            ))}
          </div>
        </>
      )}
    </section>
  );
};

export default HeroBanners;
